/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import config from '../../../../share/config';
import session from '../../../../share/session';

/** 各フェーズ定義 */
const PHASE = {
  START: 'start',
  SUCCESS: 'success',
  FAILED: 'failed'
};

export default {
  name: 'requirement128',
  data() {
    return {
      phase: PHASE.START,
      systemId: '',
      isLoading: false,
      isErrorCheck: false,
      modal_requirement_128: false
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.FAILED:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.START:
        default:
          return 'secondary';
      }
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_requirement_128 = true;
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      // 終了処理エラー判定フラグ(初期状態はエラー判定無し)
      this.isErrorCheck = false;
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_128 = false;
    },

    /**
    * API 通信で共通バリデーションでエラーが発生した際のコールバック
    * @param {object} result バリデーション結果
    */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; if (this.isErrorCheck) this.showUnexpectedError(); },
      );
    },

    /** 実行処理 */
    async executeProcess() {
      try {
        this.isLoading = true;

        // 終了処理でエラー判定を行う
        this.isErrorCheck = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'execute'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // exit処理を呼び出す
              await this.exit();
              break;
            case config.STATUS_WSP_FAILED:
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * exit処理
     */
    async exit() {
      try {
        this.isLoading = true;
        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'exit'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; if (this.isErrorCheck) this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        // 終了処理のエラー判定を行わない場合
        if (!this.isErrorCheck) {
          this.closeWizard();
          return;
        }

        // 終了処理のエラー判定を行う場合
        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // 正常終了：停止画面に遷移
              this.phase = PHASE.SUCCESS;
              break;
            case config.STATUS_WSP_FAILED:
            default:
              // 異常終了：共通エラー
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 共通エラー画面では終了処理でエラー判定を行わない
      this.isErrorCheck = false;
      // 共通エラー画面に遷移
      this.phase = PHASE.FAILED;
      return;
    },

  },

  async mounted() {
    this.init();
  },
};